import { Controller } from "@hotwired/stimulus"

/**
 * Theme Controller
 *
 * Manages dark/light mode theme switching with:
 * - localStorage for instant access (prevents flash on page load)
 * - System preference detection via prefers-color-scheme
 * - Server sync for logged-in users
 *
 * Theme values: "system" (default), "light", "dark"
 *
 * Usage:
 *   <html data-controller="theme">
 *     <button data-action="click->theme#setLight">Light</button>
 *     <button data-action="click->theme#setDark">Dark</button>
 *     <button data-action="click->theme#setSystem">System</button>
 *   </html>
 *
 * @example Setting theme programmatically
 *   this.application.getControllerForElementAndIdentifier(
 *     document.documentElement,
 *     'theme'
 *   ).setTheme('dark')
 */
export default class extends Controller {
  static values = {
    syncUrl: String,     // URL to sync theme preference to server (optional)
    current: String      // Current theme from server (for logged-in users)
  }

  static targets = ["indicator"]

  /**
   * Called when controller connects to DOM.
   * Sets up system preference listener and initializes theme state.
   *
   * @returns {void}
   */
  connect() {
    // Listen for system preference changes
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.boundSystemChange = this.handleSystemChange.bind(this)
    this.mediaQuery.addEventListener("change", this.boundSystemChange)

    // Initialize from server value if provided, otherwise from localStorage
    if (this.hasCurrentValue && this.currentValue) {
      this.theme = this.currentValue
      localStorage.setItem("theme", this.currentValue)
    } else {
      this.theme = localStorage.getItem("theme") || "system"
    }

    this.applyTheme()
    this.updateIndicators()
  }

  /**
   * Called when controller disconnects from DOM.
   * Cleans up event listeners.
   *
   * @returns {void}
   */
  disconnect() {
    this.mediaQuery.removeEventListener("change", this.boundSystemChange)
  }

  /**
   * Sets theme to light mode.
   *
   * @param {Event} event - Click event (optional)
   * @returns {void}
   */
  setLight(event) {
    event?.preventDefault()
    this.setTheme("light")
  }

  /**
   * Sets theme to dark mode.
   *
   * @param {Event} event - Click event (optional)
   * @returns {void}
   */
  setDark(event) {
    event?.preventDefault()
    this.setTheme("dark")
  }

  /**
   * Sets theme to follow system preference.
   *
   * @param {Event} event - Click event (optional)
   * @returns {void}
   */
  setSystem(event) {
    event?.preventDefault()
    this.setTheme("system")
  }

  /**
   * Sets the theme and persists to localStorage and server.
   *
   * @param {string} theme - Theme value: "system", "light", or "dark"
   * @returns {void}
   */
  setTheme(theme) {
    this.theme = theme
    localStorage.setItem("theme", theme)
    this.applyTheme()
    this.updateIndicators()
    this.syncToServer()
  }

  /**
   * Applies the current theme by toggling the .dark class on <html>.
   *
   * @returns {void}
   * @private
   */
  applyTheme() {
    const isDark = this.shouldBeDark()
    document.documentElement.classList.toggle("dark", isDark)
  }

  /**
   * Determines if dark mode should be active based on theme setting.
   *
   * @returns {boolean} True if dark mode should be active
   * @private
   */
  shouldBeDark() {
    if (this.theme === "dark") return true
    if (this.theme === "light") return false
    // System preference
    return this.mediaQuery.matches
  }

  /**
   * Handles system preference changes when theme is set to "system".
   *
   * @param {MediaQueryListEvent} event - Media query change event
   * @returns {void}
   * @private
   */
  handleSystemChange(event) {
    if (this.theme === "system") {
      this.applyTheme()
    }
  }

  /**
   * Updates visual indicators (buttons, icons) to show current theme.
   *
   * @returns {void}
   * @private
   */
  updateIndicators() {
    if (!this.hasIndicatorTarget) return

    this.indicatorTargets.forEach(indicator => {
      const indicatorTheme = indicator.dataset.theme
      const isActive = indicatorTheme === this.theme
      indicator.classList.toggle("bg-gray-200", isActive && !this.shouldBeDark())
      indicator.classList.toggle("dark:bg-gray-700", isActive)
      indicator.setAttribute("aria-pressed", isActive)
    })
  }

  /**
   * Syncs theme preference to server for logged-in users.
   * Sends PATCH request to syncUrl if provided.
   *
   * @returns {void}
   * @private
   */
  syncToServer() {
    if (!this.hasSyncUrlValue || !this.syncUrlValue) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.syncUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({ user: { preferred_theme: this.theme } })
    }).catch(error => {
      // Silently fail - localStorage still works as fallback
      console.warn("Failed to sync theme preference:", error)
    })
  }

  /**
   * Gets the current theme setting.
   *
   * @returns {string} Current theme: "system", "light", or "dark"
   */
  get currentTheme() {
    return this.theme
  }

  /**
   * Gets whether dark mode is currently active.
   *
   * @returns {boolean} True if dark mode is active
   */
  get isDark() {
    return this.shouldBeDark()
  }
}


